import React, { FormEvent, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPasswordValid } from 'dash-common';
import {
  Form,
  Button,
  useFormState,
  useFormValidation,
  FormErrorTypes,
  AlertsContext,
  Alerts,
  InputPassword,
} from 'dash-ui';
import { useResetPasswordMutation } from 'gql/generated.graphql';
import { RoutesPath } from 'flow-components/routes/routes';

enum FormFields {
  NewPassword = 'newPassword',
  ConfirmPassword = 'confirmPassword',
}

const FormFieldsList = [FormFields.NewPassword, FormFields.ConfirmPassword];

function ResetPasswordForm() {
  const { t } = useTranslation('forms');
  const { t: errorSlugs } = useTranslation('common', {
    keyPrefix: 'serverMessages',
  });
  const [searchParams] = useSearchParams();
  const { toggleAlert } = useContext(AlertsContext);
  const [formErrors, , [setNewPasswordError, setConfirmPasswordError]] =
    useFormValidation(FormFieldsList);
  const navigate = useNavigate();

  const [
    formState,
    [setNewPassword, setConfirmPassword],
    [formOnSubmit, formOnError, formOnSuccess],
  ] = useFormState(FormFieldsList);
  const [resetPasswordMutation, { loading }] = useResetPasswordMutation({
    context: {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Reset: searchParams.get('token'),
      },
    },
  });

  const validNewPassword = (value: string) => {
    if (!isPasswordValid(value)) {
      debugger;
      setNewPasswordError({
        type: FormErrorTypes.Invalid,
        message: t`messages.passwordInvalid`,
      });
      return false;
    }
    setNewPasswordError({
      type: FormErrorTypes.Valid,
      message: t`messages.newPasswordValid`,
    });
    return true;
  };

  const validConfirmPassword = (value: string) => {
    if (formState.newPassword !== value) {
      setConfirmPasswordError({
        type: FormErrorTypes.Invalid,
        message: t`messages.confirmPasswordInValid`,
      });
      return false;
    }
    setConfirmPasswordError({
      type: FormErrorTypes.Valid,
      message: t`messages.confirmPasswordValid`,
    });
    return true;
  };

  const onNewPasswordTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
  };

  const onConfirmPasswordTextChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
    validConfirmPassword(e.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { newPassword, confirmPassword } = formState;
    if (!validNewPassword(newPassword)) {
      return null;
    }
    if (!validConfirmPassword(confirmPassword)) {
      return null;
    }
    return resetPasswordMutation({
      variables: { newPassword },
    })
      .then(() => {
        formOnSuccess();
        toggleAlert(Alerts.PasswordReset, true);
        navigate(RoutesPath.SignIn);
      })
      .catch((error) => {
        formOnError(errorSlugs(error.message) || errorSlugs`unknown` || '');
      });
  };

  return (
    <Form onSubmit={onSubmit} w={334}>
      <Form.Group mt={1}>
        <Form.Label>{t`labels.newPassword`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.newPassword` || ''}
          onChange={onNewPasswordTextChange}
          $errorType={formErrors[FormFields.NewPassword]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.NewPassword]?.type}>
          {formErrors[FormFields.NewPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={1}>
        <Form.Label>{t`labels.confirmPassword`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.confirmPassword` || ''}
          onChange={onConfirmPasswordTextChange}
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        />
        <Form.Feedback
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        >
          {formErrors[FormFields.ConfirmPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Button w="100%" type="submit" disabled={loading} isLoading={loading}>
          {t`labels.resetPassword`}
        </Button>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
    </Form>
  );
}

export default ResetPasswordForm;
